import { createContext, ReactNode, useContext, useMemo } from 'react';

export interface IPaymentRestoreContext {
	restorePage: string;
	restoreQuery?: string;
}

const PaymentRestoreContext = createContext<IPaymentRestoreContext | null>(
	null
);

interface IPaymentRestoreProviderProps {
	children: ReactNode;
	restorePage: string;
	restoreQuery?: string;
}

/**
 * Provider that holds information about the target page to return to after redirect-based payment flows (Stripe redirect-based PM & TrueLayer).
 *
 * `restorePage` and `restoreQuery` are used to build redirect URLs:
 * - `useRedirectToTrueLayer()` - packs into TrueLayer OAuth `state` parameter
 * - `useStripeRedirectUrl()` - adds to Stripe redirect URL
 *
 * Purpose:
 * - **Passenger app: WEB**: restores page context after full redirect (pathname + query)
 * - **Payment app: in-app browser**: preserves query parameters during in-app browser redirect
 * */
export const PaymentRestoreProvider = ({
	children,
	restorePage,
	restoreQuery,
}: IPaymentRestoreProviderProps) => {
	const ctx = useMemo<IPaymentRestoreContext>(
		() => ({ restorePage, restoreQuery }),
		[restorePage, restoreQuery]
	);

	return (
		<PaymentRestoreContext.Provider value={ctx}>
			{children}
		</PaymentRestoreContext.Provider>
	);
};

export const usePaymentRestore = (): IPaymentRestoreContext => {
	const context = useContext(PaymentRestoreContext);

	if (!context) {
		throw new Error(
			'usePaymentRestore must be used within PaymentRestoreProvider'
		);
	}

	return context;
};
